import { motion, useReducedMotion } from 'motion/react';
import { useCopy } from '@/i18n';
import { Section } from './Section';
import type { PricingLayoutKey } from '@/shared/tweaks-defaults';
import { hasSplitTitle, renderSplitTitle, type SplitTitleCopy } from './titleCopy';

type Tier = {
  name: string;
  price: string;
  period?: string;
  blurb?: string;
  features?: string[];
  cta?: string;
  highlight?: boolean;
  badge?: string;
};

type PricingCopy = {
  free?: Tier;
  day_pass?: Tier;
  daypass?: Tier;
  premium?: Tier;
  forever_pack?: Tier;
  teams?: Tier;
};

type SectionCopy = SplitTitleCopy & {
  eyebrow?: string;
  lead?: string;
  most_chosen?: string;
};

type TableRow = { label: string; values: string[] };

export function PricingSection({ layout }: { layout: PricingLayoutKey }) {
  const copy = useCopy();
  const section = copy<SectionCopy>('pricing_section');
  const pricing = copy<PricingCopy>('pricing') ?? {};
  const tableRowsValue = copy<unknown>('pricing_table_rows');
  const normalizeTier = (tier: Tier | undefined): Tier | null => {
    if (!tier) return null;
    return {
      ...tier,
      blurb: tier.blurb ?? (tier as Tier & { sub?: string }).sub,
      features: tier.features ?? (tier as Tier & { perks?: string[] }).perks,
      highlight:
        tier.highlight ??
        (typeof (tier as Tier & { tag?: string }).tag === 'string' &&
          (tier as Tier & { tag?: string }).tag === section?.most_chosen),
      badge: (tier as Tier & { tag?: string }).tag,
    };
  };
  const tiers = [
    normalizeTier(pricing.free),
    normalizeTier(pricing.day_pass ?? pricing.daypass),
    normalizeTier(pricing.premium),
    normalizeTier(pricing.forever_pack ?? pricing.teams),
  ].filter(Boolean) as Tier[];
  const tableRows = Array.isArray(tableRowsValue)
    ? tableRowsValue.flatMap((row) => {
        if (!row || typeof row !== 'object') return [];
        const tableRow = row as Record<string, unknown>;
        if (typeof tableRow.label === 'string' && Array.isArray(tableRow.values)) {
          return [
            {
              label: tableRow.label,
              values: tableRow.values.filter((value): value is string => typeof value === 'string'),
            },
          ];
        }
        if (typeof tableRow.label === 'string') {
          return [
            {
              label: tableRow.label,
              values: [tableRow.free, tableRow.day, tableRow.premium, tableRow.teams].filter(
                (value): value is string => typeof value === 'string'
              ),
            },
          ];
        }
        return [];
      })
    : [];

  return (
    <Section id="pricing" bg="paper">
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        {section?.eyebrow ? <span className="eyebrow">{section.eyebrow}</span> : null}
        {hasSplitTitle(section) ? (
          <h2 className="h-section" style={{ marginTop: 14 }}>
            {renderSplitTitle(section, (accent) => (
              <span className="accent">{accent}</span>
            ))}
          </h2>
        ) : null}
        {section?.lead ? (
          <p className="lead" style={{ maxWidth: '52ch', margin: '18px auto 0' }}>
            {section.lead}
          </p>
        ) : null}
      </div>

      {layout === 'cards' ? <PricingCards tiers={tiers} /> : null}
      {layout === 'table' ? <PricingTable tiers={tiers} rows={tableRows} /> : null}
      {layout === 'toggle' ? <PricingCards tiers={tiers} /> : null}
    </Section>
  );
}

// Tier cards restyled after reactbits-pro pricing-11: generous rounded panels,
// large display-serif prices, the highlighted tier as a dark panel, pill CTAs.
function PricingCards({ tiers }: { tiers: Tier[] }) {
  const reduceMotion = useReducedMotion() ?? false;
  return (
    <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {tiers.map((t, i) => (
        <motion.article
          key={i}
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: reduceMotion ? 0 : 0.5, delay: reduceMotion ? 0 : i * 0.06 }}
          className="flex flex-col rounded-3xl p-6 sm:p-8"
          style={{
            background: t.highlight ? 'var(--ink)' : 'var(--paper-2)',
            color: t.highlight ? 'var(--paper)' : 'var(--ink)',
            border: t.highlight ? 'none' : '1px solid var(--line)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22 }}>{t.name}</h3>
            {t.badge ? (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: t.highlight ? 'var(--coral)' : 'var(--sand)',
                  color: t.highlight ? '#fff' : 'var(--ink)',
                }}
              >
                {t.badge}
              </span>
            ) : null}
          </div>
          <div style={{ marginTop: 14 }}>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(40px, 4vw, 56px)',
                lineHeight: 1,
              }}
            >
              {t.price}
            </span>
            {t.period ? (
              <span style={{ fontSize: 14, opacity: 0.7, marginInlineStart: 6 }}>{t.period}</span>
            ) : null}
          </div>
          {t.blurb ? (
            <p style={{ marginTop: 12, fontSize: 13, lineHeight: 1.5, opacity: 0.85 }}>{t.blurb}</p>
          ) : null}
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '16px 0',
              display: 'grid',
              gap: 8,
              flex: 1,
            }}
          >
            {(Array.isArray(t.features) ? t.features : []).map((f, j) => (
              <li key={j} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                <span style={{ color: t.highlight ? 'var(--coral)' : 'var(--leaf)' }}>✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {t.cta ? (
            <motion.a
              href="/signup"
              whileHover={reduceMotion ? undefined : { scale: 1.03 }}
              whileTap={reduceMotion ? undefined : { scale: 0.97 }}
              className={t.highlight ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ width: '100%', justifyContent: 'center', borderRadius: 999 }}
            >
              {t.cta}
            </motion.a>
          ) : null}
        </motion.article>
      ))}
    </div>
  );
}

function PricingTable({ tiers, rows }: { tiers: Tier[]; rows: TableRow[] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'start', padding: '12px 14px' }}></th>
          {tiers.map((t, i) => (
            <th
              key={i}
              style={{
                textAlign: 'start',
                padding: '12px 14px',
                fontFamily: 'var(--font-display)',
                fontSize: 16,
              }}
            >
              {t.name}
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>{t.price}</div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} style={{ borderTop: '1px solid var(--line)' }}>
            <td style={{ padding: '12px 14px', fontWeight: 500 }}>{row.label}</td>
            {(Array.isArray(row.values) ? row.values : []).map((v, j) => (
              <td key={j} style={{ padding: '12px 14px' }}>
                {v}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
